<template>
  <div class="relative w-full">
    <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <g clip-path="url(#clip0_5896_62907)">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M0 2C0 1.46957 0.210714 0.960859 0.585786 0.585786C0.960859 0.210714 1.46957 0 2 0L14 0C14.5304 0 15.0391 0.210714 15.4142 0.585786C15.7893 0.960859 16 1.46957 16 2V14C16 14.5304 15.7893 15.0391 15.4142 15.4142C15.0391 15.7893 14.5304 16 14 16H2C1.46957 16 0.960859 15.7893 0.585786 15.4142C0.210714 15.0391 0 14.5304 0 14V2ZM15 4H11V7H15V4ZM15 8H11V11H15V8ZM15 12H11V15H14C14.2652 15 14.5196 14.8946 14.7071 14.7071C14.8946 14.5196 15 14.2652 15 14V12ZM10 15V12H6V15H10ZM5 15V12H1V14C1 14.2652 1.10536 14.5196 1.29289 14.7071C1.48043 14.8946 1.73478 15 2 15H5ZM1 11H5V8H1V11ZM1 7H5V4H1V7ZM6 4V7H10V4H6ZM10 8H6V11H10V8Z" fill="#737373"/>
        </g>
        <defs>
          <clipPath id="clip0_5896_62907">
            <rect width="16" height="16" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    </div>
    <input
      ref="inputRef"
      type="text"
      :placeholder="placeholder"
      :disabled="disabled"
      :class="[
        'h-11 text-custom_gray text-base rounded-lg focus:ring-tuin_base-0 focus:border-tuin_base-0 block w-full ps-9 pe-3 p-2.5 cursor-pointer',
        props.error ? 'bg-red-50 border-red-500' : 'bg-white border-gray-300'
      ]"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";

const props = defineProps({
  modelValue: {
    default: null,
  },
  placeholder: {
    type: String,
    default: "Selecciona una fecha",
  },
  format: {
    type: String,
    default: "dd/mm/yyyy",
  },
  orientation: {
    type: String,
    default: "bottom",
  },
  minDate: {
    type: String,
    default: null,
  },
  maxDate: {
    type: String,
    default: null,
  },
  autohide: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false 
  }
});

const emit = defineEmits(["update:modelValue"]);

const inputRef = ref(null);
let datepickerInstance = null;
let isInternalUpdate = false;

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
}

function formatDateForEmit(date) {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateForDisplay(date) {
  if (!date) return "";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function initDatepicker() {
  if (!inputRef.value) return;

  Promise.all([
    import("flowbite"),
    import("flowbite-datepicker"),
  ]).then(([{ Datepicker }, { Datepicker: BaseDatepicker }]) => {
    BaseDatepicker.locales.es = {
      days: ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"],
      daysShort: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
      daysMin: ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sa"],
      months: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"],
      monthsShort: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
      today: "Hoy",
      monthsTitle: "Meses",
      clear: "Borrar",
      weekStart: 1,
      format: "dd/mm/yyyy",
    };

    const options = {
      format: props.format,
      autohide: props.autohide,
      orientation: props.orientation,
      language: "es",
    };

    if (props.minDate) options.minDate = props.minDate;
    if (props.maxDate) options.maxDate = props.maxDate;

    datepickerInstance = new Datepicker(inputRef.value, options);

    if (props.modelValue) {
      const parsed = parseDate(props.modelValue);
      if (parsed) {
        isInternalUpdate = true;
        datepickerInstance.setDate(parsed, { render: false });
        inputRef.value.value = formatDateForDisplay(parsed);
        isInternalUpdate = false;
      }
    }

    inputRef.value.addEventListener("changeDate", handleChangeDate);
  });
}

function handleChangeDate(event) {
  if (isInternalUpdate) return;
  const date = event.detail.date;
  emit("update:modelValue", formatDateForEmit(date));
}

watch(
  () => props.modelValue,
  (newVal) => {
    if (!datepickerInstance) return;
    const parsed = parseDate(newVal);
    const currentDate = datepickerInstance.getDate();
    const currentDateStr = currentDate
      ? formatDateForEmit(currentDate)
      : "";
    const newValStr = parsed ? formatDateForEmit(parsed) : "";

    if (currentDateStr !== newValStr) {
      isInternalUpdate = true;
      if (parsed) {
        datepickerInstance.setDate(parsed, { render: false });
        if (inputRef.value) {
          inputRef.value.value = formatDateForDisplay(parsed);
        }
      } else {
        datepickerInstance.setDate({ clear: true });
        if (inputRef.value) {
          inputRef.value.value = "";
        }
      }
      isInternalUpdate = false;
    }
  }
);

onMounted(() => {
  nextTick(() => {
    initDatepicker();
  });
});

onUnmounted(() => {
  if (inputRef.value) {
    inputRef.value.removeEventListener("changeDate", handleChangeDate);
  }
  if (datepickerInstance) {
    datepickerInstance.destroy();
    datepickerInstance = null;
  }
});

defineExpose({
  show: () => datepickerInstance?.show(),
  hide: () => datepickerInstance?.hide(),
  getDate: () => datepickerInstance?.getDate() ?? null,
  setDate: (date) => {
    if (datepickerInstance) {
      datepickerInstance.setDate(date);
      emit("update:modelValue", formatDateForEmit(datepickerInstance.getDate()));
    }
  },
});
</script>
<style>
.datepicker-cell.selected,
.datepicker-cell.selected:hover {
  background-color: #FF4265 !important;
  color: white !important;
}
.datepicker-cell.selected.focused {
  background-color: #FF4265 !important;
}
</style>
